import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectCurrentUser, selectIsAuthenticated } from '../store/slices/authSlice';
import PinModal from '../components/PinModal';
import { Toast } from '../components/Toast';

function UserProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const currentUser = useSelector(selectCurrentUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const token = useSelector((state) => state.auth.token);
  
  const [user, setUser] = useState(null);
  const [userPins, setUserPins] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const [selectedPinId, setSelectedPinId] = useState(null);
  const [activeTab, setActiveTab] = useState('pins');

  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    loadUserProfile();
    loadUserPins();
    if (isAuthenticated && currentUser?.username !== userId) {
      checkFollowStatus();
    }
  }, [userId]);

  const loadUserProfile = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/user/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setUser(data);
        setFollowersCount(data.followers?.length || 0);
        setFollowingCount(data.following?.length || 0);
      } else {
        Toast.error('Không tìm thấy người dùng');
        navigate('/');
      }
    } catch (error) {
      console.error('Error loading user:', error);
      Toast.error('Lỗi khi tải thông tin người dùng');
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserPins = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/pins/user/${userId}/all`);
      if (response.ok) {
        const data = await response.json();
        // Only show approved pins for other users
        const filteredPins = currentUser?.username === userId 
          ? data 
          : data.filter(pin => pin.status === 'approved');
        setUserPins(filteredPins);
      }
    } catch (error) {
      console.error('Error loading pins:', error);
    }
  };

  const checkFollowStatus = async () => {
    if (!currentUser?.username) return;
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/auth/following/${userId}?currentUsername=${currentUser.username}`
      );
      if (response.ok) {
        const data = await response.json();
        setIsFollowing(data.isFollowing);
      }
    } catch (error) {
      console.error('Error checking follow status:', error);
    }
  };

  const handleFollow = async () => {
    if (!isAuthenticated) {
      Toast.warning('Vui lòng đăng nhập để theo dõi!');
      navigate('/login');
      return;
    }

    if (isFollowLoading) return;

    setIsFollowLoading(true);
    try {
      const endpoint = isFollowing ? 'unfollow' : 'follow';
      const response = await fetch(`${API_BASE_URL}/auth/${endpoint}/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setIsFollowing(!isFollowing);
        setFollowersCount(data.followers);
        Toast.success(isFollowing ? 'Đã bỏ theo dõi' : 'Đã theo dõi');
      } else {
        const error = await response.json();
        Toast.error(error.error || 'Không thể thực hiện');
      }
    } catch (error) {
      console.error('Error following user:', error);
      Toast.error('Lỗi khi thực hiện');
    } finally {
      setIsFollowLoading(false);
    }
  };

  const isOwnProfile = currentUser?.username === userId;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Đang tải...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <span className="material-symbols-rounded text-6xl text-gray-300 dark:text-gray-600 mb-4 block">
            person_off
          </span>
          <p className="text-gray-500 dark:text-gray-400 text-lg">Không tìm thấy người dùng</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Profile Header */}
      <div className="text-center mb-6 sm:mb-8">
        <div className="relative inline-block mb-3 sm:mb-4">
          <img
            src={user.avatar || `https://api.dicebear.com/7.x/notionists/svg?seed=${user.username}&backgroundColor=e9e9e9`}
            alt={user.displayName}
            className="w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 rounded-full bg-gray-100 border-4 border-white dark:border-gray-700 shadow-lg"
          />
        </div>
        
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
          {user.displayName || user.username}
        </h1>
        
        <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 mb-3 sm:mb-4">
          @{user.username}
        </p>
        
        {user.bio && (
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 max-w-md mx-auto mb-4 px-4">
            {user.bio}
          </p>
        )}
        
        {/* Stats */}
        <div className="flex items-center justify-center gap-4 sm:gap-6 text-xs sm:text-sm mb-4">
          <div>
            <span className="font-bold text-gray-900 dark:text-white">{userPins.length}</span>
            <span className="text-gray-500 dark:text-gray-400"> Prompts</span>
          </div>
          <div className="cursor-pointer hover:text-blue-600 transition-colors">
            <span className="font-bold text-gray-900 dark:text-white">{followersCount}</span>
            <span className="text-gray-500 dark:text-gray-400"> Người theo dõi</span>
          </div>
          <div className="cursor-pointer hover:text-blue-600 transition-colors">
            <span className="font-bold text-gray-900 dark:text-white">{followingCount}</span>
            <span className="text-gray-500 dark:text-gray-400"> Đang theo dõi</span>
          </div>
        </div>

        {/* Follow Button */}
        {!isOwnProfile && (
          <button
            onClick={handleFollow}
            disabled={isFollowLoading}
            className={`px-8 py-2.5 rounded-full font-semibold transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${
              isFollowing
                ? 'bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white'
                : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg'
            }`}
          >
            {isFollowLoading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                Đang xử lý...
              </span>
            ) : (
              <>
                {isFollowing ? (
                  <span className="flex items-center gap-2">
                    <span className="material-symbols-rounded text-[20px]">check</span>
                    Đang theo dõi
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <span className="material-symbols-rounded text-[20px]">add</span>
                    Theo dõi
                  </span>
                )}
              </>
            )}
          </button>
        )}

        {isOwnProfile && (
          <button
            onClick={() => navigate('/profile')}
            className="px-8 py-2.5 rounded-full font-semibold bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white transition-all"
          >
            Chỉnh sửa trang cá nhân
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <div className="flex gap-8 justify-center">
          <button
            onClick={() => setActiveTab('pins')}
            className={`pb-4 px-2 border-b-2 font-semibold transition-colors ${
              activeTab === 'pins'
                ? 'border-gray-900 dark:border-gray-100 text-gray-900 dark:text-white'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <span className="flex items-center gap-2">
              <span className="material-symbols-rounded text-[20px]">grid_view</span>
              Prompts
            </span>
          </button>
        </div>
      </div>

      {/* Pins Grid */}
      <div className="mb-12">
        {userPins.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 dark:bg-gray-800 rounded-xl">
            <span className="material-symbols-rounded text-6xl text-gray-300 dark:text-gray-600 mb-4 block">
              add_circle
            </span>
            <p className="text-gray-500 dark:text-gray-400 text-lg">
              {isOwnProfile ? 'Bạn chưa có prompt nào' : 'Người dùng chưa có prompt nào'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-4">
            {userPins.map((pin) => (
              <div key={pin._id} className="relative group">
                <div
                  onClick={() => setSelectedPinId(pin._id)}
                  className="cursor-pointer rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow bg-white dark:bg-gray-800"
                >
                  <img
                    src={pin.image}
                    alt={pin.title}
                    className="w-full h-auto object-cover"
                  />
                  <div className="p-3">
                    <h3 className="font-semibold text-sm text-gray-900 dark:text-white line-clamp-2">
                      {pin.title}
                    </h3>
                    {isOwnProfile && (
                      <div className="mt-2">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                          pin.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                          pin.status === 'approved' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                          'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}>
                          {pin.status === 'pending' ? 'Chờ duyệt' : pin.status === 'approved' ? 'Đã duyệt' : 'Từ chối'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pin Modal */}
      {selectedPinId && (
        <PinModal pinId={selectedPinId} onClose={() => setSelectedPinId(null)} />
      )}
    </div>
  );
}

export default UserProfile;
